import { flash } from "@xola/ui-kit";
import React from "react";
import { useMutation } from "react-query";
import { ApiKeyUser } from "types";
import { api } from "@/api/api";
import { PromptModal } from "@/components/Common/PromptModal";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useModal } from "@/hooks/useVisibility";
import { getSeller } from "@/utils/getSeller";

interface RevokeApiKeyButtonProps {
    apiKey?: ApiKeyUser;
    onSuccess?: () => void;
}

export const RevokeApiKeyButton = ({ apiKey, onSuccess }: RevokeApiKeyButtonProps) => {
    const invalidate = useInvalidate();
    const revokeModal = useModal(false);

    const { mutateAsync: deleteApiKey, isLoading } = useMutation(async (apiKeyId: ID) => {
        return api.delete(`/sellers/${getSeller().id}/apiKeys/${apiKeyId}`, { suppressErrorToast: true });
    });

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        revokeModal.show();
    };

    const handleDeleteConfirm = async () => {
        if (!apiKey?.id) {
            revokeModal.hide();
            return;
        }

        try {
            await deleteApiKey(apiKey.id);
            invalidate("apiKeys", apiKey.id);
            onSuccess?.();
            flash.success("API Key revoked");
            revokeModal.hide();
        } catch {
            flash.danger("Failed to revoke API key. Please try again.");
        }
    };

    return (
        <>
            <SecondaryButton size="small" data-testid="api-key-item-revoke-button" onClick={handleDelete}>
                Revoke Key
            </SecondaryButton>

            <PromptModal
                title="Revoke API Key?"
                isLoading={isLoading}
                isOpen={revokeModal.isVisible}
                onConfirm={handleDeleteConfirm}
                onClose={revokeModal.hide}
            >
                Are you sure you want to revoke this API key? This action cannot be undone.
            </PromptModal>
        </>
    );
};
